/**
 * Lista de membros para seleção de desativação
 * Usado na correção de discrepâncias de limites
 * 
 * Versão: 1.0.0
 * Data: 2026-01-08
 */
import { Lock, User, Shield, Crown } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useTenantMembersForAudit } from '@/hooks/useAdminTenantLimitsAudit';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface MemberSelectionListProps {
  tenantId: string;
  requiredCount: number;
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
}

/**
 * Componente de lista de membros para seleção
 */
export function MemberSelectionList({
  tenantId,
  requiredCount,
  selectedIds,
  onSelectionChange,
}: MemberSelectionListProps) {
  const { data: members, isLoading, error } = useTenantMembersForAudit(tenantId);
  
  // Toggle seleção de um membro
  const toggleMember = (memberId: string, canDeactivate: boolean) => {
    if (!canDeactivate) return;
    
    if (selectedIds.includes(memberId)) {
      // Remover da seleção
      onSelectionChange(selectedIds.filter(id => id !== memberId));
    } else if (selectedIds.length < requiredCount) {
      // Adicionar à seleção (se ainda não atingiu o limite)
      onSelectionChange([...selectedIds, memberId]);
    }
  };
  
  // Ícone do role
  const getRoleIcon = (role: string, isOwner: boolean) => {
    if (isOwner) return <Crown className="h-3.5 w-3.5 text-amber-500" />;
    if (role === 'tenant_admin' || role === 'root_admin') {
      return <Shield className="h-3.5 w-3.5 text-blue-500" />;
    }
    return <User className="h-3.5 w-3.5 text-muted-foreground" />;
  };
  
  // Label do role
  const getRoleLabel = (role: string, isOwner: boolean) => {
    if (isOwner) return 'Proprietário';
    switch (role) {
      case 'root_admin': return 'Root Admin';
      case 'tenant_admin': return 'Admin';
      case 'supervisor': return 'Supervisor';
      case 'agent': return 'Agente';
      default: return role;
    }
  };
  
  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3, 4, 5].map(i => (
          <div key={i} className="flex items-center gap-3 p-3 border rounded-lg">
            <Skeleton className="h-4 w-4" />
            <Skeleton className="h-10 w-10 rounded-full" />
            <div className="flex-1 space-y-1">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-3 w-48" />
            </div>
          </div>
        ))}
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="text-center py-8 text-destructive">
        Erro ao carregar membros: {error.message}
      </div>
    );
  }
  
  if (!members || members.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        Nenhum membro encontrado
      </div>
    );
  }
  
  const selectionComplete = selectedIds.length === requiredCount;
  
  return (
    <div className="space-y-2">
      {/* Instrução */}
      <div className="text-sm text-muted-foreground mb-4">
        Selecione <strong>{requiredCount}</strong> membro(s) para desativar.
        {selectionComplete && (
          <span className="text-green-600 ml-2">✓ Seleção completa</span>
        )}
      </div>
      
      {/* Lista de membros */}
      <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
        {members.map((member) => {
          const isSelected = selectedIds.includes(member.member_id);
          const canSelect = member.can_deactivate && (isSelected || selectedIds.length < requiredCount);
          
          return (
            <div
              key={member.member_id}
              className={`
                flex items-center gap-3 p-3 border rounded-lg transition-colors
                ${member.can_deactivate ? 'cursor-pointer hover:bg-muted/50' : 'opacity-60 cursor-not-allowed bg-muted/30'}
                ${isSelected ? 'border-destructive bg-destructive/5' : ''}
              `}
              onClick={() => toggleMember(member.member_id, canSelect)}
            >
              {/* Checkbox ou Lock */}
              {member.can_deactivate ? (
                <Checkbox
                  checked={isSelected}
                  disabled={!canSelect}
                  onCheckedChange={() => toggleMember(member.member_id, canSelect)}
                />
              ) : (
                <Lock className="h-4 w-4 text-muted-foreground" />
              )}
              
              {/* Avatar placeholder */}
              <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
                {getRoleIcon(member.role, member.is_owner)}
              </div>
              
              {/* Info do membro */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium truncate">{member.full_name}</span>
                  <Badge variant="outline" className="text-xs">
                    {getRoleLabel(member.role, member.is_owner)}
                  </Badge>
                  {!member.is_active && (
                    <Badge variant="secondary" className="text-xs">Inativo</Badge>
                  )}
                </div>
                <div className="text-xs text-muted-foreground truncate">
                  {member.email}
                </div>
              </div>
              
              {/* Data de entrada */}
              <div className="text-xs text-muted-foreground text-right">
                {format(new Date(member.joined_at), "dd/MM/yyyy", { locale: ptBR })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
